import React, { useEffect, useRef } from "react";
import { Stack, Typography } from "@mui/joy";
import FullPageContainer from "./FullPageContainer";
import { theme } from "styles/theme";
import BackButton from "components/common/BackButton";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import { useOnboardingProgress } from "hooks/useOnboardingProgress";
import { extractHexColor } from "styles/hexOpacity";
import ProgressBarWithSection from "../../components/onboarding/ProgressBarWithSection";
import CloseButtonSmall from "../../components/task/change-status-button/CloseButtonSmall";

export default function FullPageWithHeaderAndProgress({
  headerTitle,
  hideHeader,
  hideProgress,
  backgroundColor = theme.vars.palette.Background.Level1,
  body,
  disableBack,
  hideBack = false,
  onBack,
  hideClose = false,
  paddingXContent = 20,
  onClose,
}: {
  headerTitle?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  backgroundColor?: string;
  body?: React.ReactNode;
  disableBack?: boolean;
  hideBack?: boolean;
  onBack?: () => void;
  hideClose?: boolean;
  paddingXContent?: number;
  onClose?: () => void;
}) {
  const headerHeight = 60; // 헤더 높이
  const progressBarHeight = 12; // 프로그레스바 높이
  const progressPaddingX = 20; // 프로그래스바 양쪽 패딩 값

  const paddingXHeader = 8;
  const paddingTopContent = hideProgress
    ? hideHeader
      ? headerHeight + 8
      : 8
    : hideHeader
      ? headerHeight + 32
      : 32;

  const paddingBottomContent = hideProgress ? 8 : 32;
  const containerRef = useRef<HTMLDivElement>(null);

  // 화면 잘림 문제
  useEffect(() => {
    // 페이지 전환(혹은 특정 이벤트) 시점에
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [body]);

  const isInappWebview = useIsInappWebview();

  const { progress, sectionCount } = useOnboardingProgress();

  useEffect(() => {
    const backgroundHex = extractHexColor(backgroundColor);
    if (!backgroundHex) return;

    // In-app WebView: native safe-area fill
    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${backgroundHex}`,
            bottomColor: `${backgroundHex}`,
          },
        })
      );
      return;
    }

    // Mobile web browsers: update theme-color meta tag
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement("meta");
      metaThemeColor.setAttribute("name", "theme-color");
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute("content", backgroundHex);
  }, [backgroundColor, isInappWebview]);

  return (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100vw",
          mx: "auto",
          height: "100vh",
        }}
      >
        {!hideHeader && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              px: `${paddingXHeader}px`,
              position: "sticky",
              top: 0,
              zIndex: 100,
              height: `${headerHeight}px`, // 헤더 높이 설정
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {!hideBack && onBack && (
                <BackButton onBack={onBack} disabled={disableBack} />
              )}
              {headerTitle && (
                <Typography
                  variant="Body"
                  sx={{
                    px: 0,
                    fontColor: theme.vars.palette.Content.Strong,
                    fontWeight: 700,
                  }}
                >
                  {headerTitle}
                </Typography>
              )}
            </Stack>
            {!hideClose && onClose && <CloseButtonSmall />}
          </Stack>
        )}
        {!hideProgress && (
          <Stack
            sx={{
              width: "100%",
              px: `${progressPaddingX}px`,
              mt: 1,
              position: "sticky",
              top: `${headerHeight}px`, // 헤더 높이만큼 아래로 이동
              zIndex: 99,
              height: `${progressBarHeight}px`, // 프로그레스바 높이 설정
            }}
          >
            <ProgressBarWithSection
              progress={progress}
              sectionCount={sectionCount}
            />
          </Stack>
        )}
        <Stack
          ref={containerRef}
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            px: `${paddingXContent}px`,
            pt: `${paddingTopContent}px`,
            pb: `${paddingBottomContent}px`,
            position: "relative",
          }}
        >
          {body}
        </Stack>
      </Stack>
    </FullPageContainer>
  );
}
